import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useExpensesService } from 'services/expenses';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES, ROUTES } from 'utils/constants';
import type { DeductibilityStatus } from 'types/expenses';
import { Expense } from 'types/expenses';
import Category from 'types/category';
import Toast from 'components/Toast';
import Button from 'components/Button';
import styles from './ViewExpense.module.scss';

function ViewExpense() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const expensesService = useExpensesService();
  const apiClient = useApiClient();
  const [expense, setExpense] = useState<Expense | null>(null);
  const [categories, setCategories] = useState<{ [key: string]: Category }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const [deductStatus, setDeductStatus] =
    useState<DeductibilityStatus>('deductible');
  const [deductHasOr, setDeductHasOr] = useState(true);
  const [deductReason, setDeductReason] = useState('');
  const [deductAmt, setDeductAmt] = useState('');
  const [savingDeduct, setSavingDeduct] = useState(false);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Fetch categories
  const loadCategories = useCallback(async () => {
    try {
      const categoriesData = await apiClient.get<{
        [key: string]: Category;
      }>(API_ROUTES.CATEGORY);
      if (categoriesData) {
        setCategories(categoriesData);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, [apiClient]);

  // Load expense
  const loadExpense = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await expensesService.getExpenseById(id);
      setExpense(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to load expense. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [id, expensesService, showToast]);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    void loadExpense();
  }, [loadExpense]);

  useEffect(() => {
    if (!expense) return;
    setDeductStatus(expense.deductibilityStatus ?? 'deductible');
    setDeductHasOr(expense.hasOfficialReceipt ?? true);
    setDeductReason(expense.deductibilityReason ?? '');
    setDeductAmt(
      expense.deductibleAmount != null &&
        !Number.isNaN(Number(expense.deductibleAmount))
        ? String(expense.deductibleAmount)
        : '',
    );
  }, [expense]);

  const saveDeductibility = async () => {
    if (!expense || !id) return;
    const needsReason =
      deductStatus === 'non_deductible' ||
      deductStatus === 'partially_deductible';
    if (needsReason && !deductReason.trim()) {
      showToast('Deductibility reason is required for this status', 'error');
      return;
    }
    let deductibleAmount: number | undefined;
    if (deductStatus === 'partially_deductible') {
      const d = Number.parseFloat(deductAmt);
      if (Number.isNaN(d) || d <= 0) {
        showToast('Enter a valid deductible amount', 'error');
        return;
      }
      if (d > expense.totalExpense + 1e-9) {
        showToast('Deductible amount cannot exceed total expense', 'error');
        return;
      }
      deductibleAmount = d;
    }

    setSavingDeduct(true);
    try {
      const updated = await expensesService.updateExpenseDeductibility(id, {
        deductibilityStatus: deductStatus,
        hasOfficialReceipt: deductHasOr,
        ...(needsReason && { deductibilityReason: deductReason.trim() }),
        ...(deductStatus === 'partially_deductible' &&
          deductibleAmount !== undefined && { deductibleAmount }),
      });
      setExpense(updated);
      showToast('Deductibility updated', 'success');
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Failed to update',
        'error',
      );
    } finally {
      setSavingDeduct(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  // Get category name
  const getCategoryName = (categoryId: string): string => {
    return categories[categoryId]?.name || 'Unknown Category';
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>Loading expense details...</div>
      </div>
    );
  }

  if (error || !expense) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p>{error || 'Expense not found'}</p>
          <button
            className={styles.backButton}
            onClick={() => navigate(ROUTES.EXPENSES.ROOT)}
          >
            <ArrowLeft size={20} />
            Back to Expenses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <button
          className={styles.backButton}
          onClick={() => navigate(ROUTES.EXPENSES.ROOT)}
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        {expense.isVoided && <span className={styles.voidedBadge}>VOIDED</span>}
      </div>

      <div className={styles.invoiceContainer}>
        {/* Invoice Header */}
        <div className={styles.invoiceHeader}>
          <div className={styles.supplierInfo}>
            {expense.supplier ? (
              <>
                <h1 className={styles.supplierName}>{expense.supplier.name}</h1>
                {expense.supplier.email && (
                  <p className={styles.supplierDetail}>
                    {expense.supplier.email}
                  </p>
                )}
                {expense.supplier.phone && (
                  <p className={styles.supplierDetail}>
                    {expense.supplier.phone}
                  </p>
                )}
                {expense.supplier.address && (
                  <p className={styles.supplierDetail}>
                    {expense.supplier.address}
                  </p>
                )}
              </>
            ) : (
              <h1 className={styles.supplierName}>Supplier</h1>
            )}
          </div>
          <div className={styles.invoiceInfo}>
            <h2 className={styles.invoiceTitle}>EXPENSE TRANSACTION</h2>
            <div className={styles.invoiceMeta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Transaction #:</span>
                <span className={styles.metaValue}>
                  {expense.id.slice(0, 8)}
                </span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Date:</span>
                <span className={styles.metaValue}>
                  {formatDate(expense.date)}
                </span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Paid By:</span>
                <span className={styles.metaValue}>
                  {expense.company?.name || 'Company'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Expense Items Table */}
        <div className={styles.itemsSection}>
          <h3 className={styles.sectionTitle}>Expense Items</h3>
          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th className={styles.itemHeader}>#</th>
                <th className={styles.itemHeader}>Description</th>
                <th className={styles.itemHeaderAmount}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {expense.expenseEntries.map((entry, index) => (
                <tr key={`expense-${index}`} className={styles.itemRow}>
                  <td className={styles.itemNumber}>{index + 1}</td>
                  <td className={styles.itemDescription}>
                    {getCategoryName(entry.categoryId)}
                  </td>
                  <td className={styles.itemAmount}>
                    ${formatCurrency(entry.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Payment Methods Table */}
        <div className={styles.paymentSection}>
          <h3 className={styles.sectionTitle}>Payment Methods</h3>
          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th className={styles.itemHeader}>#</th>
                <th className={styles.itemHeader}>Payment Method</th>
                <th className={styles.itemHeaderAmount}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {expense.cashOrApEntries.map((entry, index) => (
                <tr key={`cash-ap-${index}`} className={styles.itemRow}>
                  <td className={styles.itemNumber}>{index + 1}</td>
                  <td className={styles.itemDescription}>
                    {getCategoryName(entry.categoryId)}
                  </td>
                  <td className={styles.itemAmount}>
                    ${formatCurrency(entry.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Notes/Description Section */}
        {expense.description && (
          <div className={styles.notesSection}>
            <label className={styles.notesLabel}>Notes</label>
            <div className={styles.notesTextarea}>{expense.description}</div>
          </div>
        )}

        {/* Totals Section */}
        <div className={styles.totalsSection}>
          <div className={styles.totalsTable}>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Subtotal:</span>
              <span className={styles.totalValue}>
                ${formatCurrency(expense.totalExpense)}
              </span>
            </div>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Total:</span>
              <span className={styles.totalValue}>
                ${formatCurrency(expense.totalExpense)}
              </span>
            </div>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Amount Paid:</span>
              <span className={styles.totalValue}>
                ${formatCurrency(expense.totalCashOrAp)}
              </span>
            </div>
          </div>
        </div>

        {/* Tax deductibility */}
        <div className={styles.deductibilitySection}>
          <h3 className={styles.deductibilityTitle}>Tax deductibility</h3>
          {!expense.isVoided ? (
            <>
              <div className={styles.deductibilityRow}>
                <span className={styles.deductibilityFieldLabel}>Status</span>
                <select
                  className={styles.deductibilitySelect}
                  value={deductStatus}
                  onChange={(e) =>
                    setDeductStatus(e.target.value as DeductibilityStatus)
                  }
                  aria-label="Deductibility status"
                >
                  <option value="deductible">Deductible</option>
                  <option value="non_deductible">Non-deductible</option>
                  <option value="partially_deductible">
                    Partially deductible
                  </option>
                </select>
              </div>
              <div className={styles.deductibilityRow}>
                <span className={styles.deductibilityFieldLabel}>
                  Official receipt (OR)
                </span>
                <div className={styles.orRadios}>
                  <label>
                    <input
                      type="radio"
                      name="view-has-or"
                      checked={deductHasOr}
                      onChange={() => {
                        setDeductHasOr(true);
                      }}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="view-has-or"
                      checked={!deductHasOr}
                      onChange={() => {
                        setDeductHasOr(false);
                        if (deductStatus === 'deductible') {
                          setDeductStatus('non_deductible');
                          setDeductReason((prev) => prev.trim() || 'No OR');
                        }
                      }}
                    />
                    No
                  </label>
                </div>
              </div>
              {!deductHasOr && (
                <p className={styles.deductibilityHint}>
                  Without an OR, non-deductible is often appropriate; you can
                  still change the status if needed.
                </p>
              )}
              {(deductStatus === 'non_deductible' ||
                deductStatus === 'partially_deductible') && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <label
                    htmlFor="view-deduct-reason"
                    className={styles.deductibilityFieldLabel}
                  >
                    Reason
                  </label>
                  <textarea
                    id="view-deduct-reason"
                    className={styles.deductibilityTextarea}
                    value={deductReason}
                    onChange={(e) => setDeductReason(e.target.value)}
                    placeholder="e.g. No OR, Personal expense"
                  />
                </div>
              )}
              {deductStatus === 'partially_deductible' && (
                <div className={styles.deductibilityRow}>
                  <span className={styles.deductibilityFieldLabel}>
                    Deductible amount
                  </span>
                  <input
                    id="view-deduct-amt"
                    type="text"
                    inputMode="decimal"
                    className={styles.deductibilityInput}
                    value={deductAmt}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) {
                        setDeductAmt(v);
                      }
                    }}
                    placeholder="0.00"
                    aria-label="Deductible amount"
                  />
                  <span className={styles.deductibilityHint}>
                    Max: ${formatCurrency(expense.totalExpense)}
                  </span>
                </div>
              )}
              <div className={styles.deductibilityActions}>
                <Button
                  type="button"
                  onClick={() => void saveDeductibility()}
                  disabled={savingDeduct}
                >
                  {savingDeduct ? 'Saving…' : 'Save deductibility'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.deductibilityRow}>
                <span className={styles.deductibilityFieldLabel}>Status</span>
                <span>
                  {expense.deductibilityStatus === 'non_deductible'
                    ? 'Non-deductible'
                    : expense.deductibilityStatus === 'partially_deductible'
                      ? 'Partially deductible'
                      : 'Deductible'}
                </span>
              </div>
              <div className={styles.deductibilityRow}>
                <span className={styles.deductibilityFieldLabel}>
                  Official receipt
                </span>
                <span>{expense.hasOfficialReceipt ? 'Yes' : 'No'}</span>
              </div>
              {expense.deductibilityReason && (
                <div className={styles.deductibilityRow}>
                  <span className={styles.deductibilityFieldLabel}>Reason</span>
                  <span>{expense.deductibilityReason}</span>
                </div>
              )}
              {expense.deductibilityStatus === 'partially_deductible' &&
                expense.deductibleAmount != null && (
                  <div className={styles.deductibilityRow}>
                    <span className={styles.deductibilityFieldLabel}>
                      Deductible amount
                    </span>
                    <span>
                      ${formatCurrency(Number(expense.deductibleAmount))}
                    </span>
                  </div>
                )}
            </>
          )}
        </div>

        {/* Void Information */}
        {expense.isVoided && expense.voidReason && (
          <div className={styles.voidInfo}>
            <h3 className={styles.voidTitle}>Void Information</h3>
            <div className={styles.voidInfoRow}>
              <strong>Voided At:</strong>
              <span>
                {expense.voidedAt ? formatDate(expense.voidedAt) : 'N/A'}
              </span>
            </div>
            <div className={styles.voidInfoRow}>
              <strong>Reason:</strong>
              <span>{expense.voidReason}</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className={styles.invoiceFooter}>
          <p className={styles.footerText}>
            This is a system-generated expense transaction. For questions,
            please contact support.
          </p>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast(null);
          }}
        />
      )}
    </div>
  );
}

export default ViewExpense;
